/**
 * AgentWiki logo: winged open book mark + "AgentWiki" wordmark.
 * agentwiki.cc only publishes a raster PNG (/logo/agentwiki-logo.png), so this is an
 * inline-SVG redraw of that mark with the site's teal → cyan gradient. The book and
 * "Agent" use currentColor so the logo follows the surrounding text color in both themes.
 */

import { useId } from 'react'
import { cn } from '../../lib/utils'

interface LogoMarkProps {
  className?: string
  title?: string
}

/** Wings + open book; decorative unless a title is given */
export function AgentWikiMark({ className, title }: LogoMarkProps) {
  const gradientId = `aw-logo-gradient-${useId().replace(/:/g, '')}`
  const wings = [
    'M10.5 9C6 8 3 5.2 1.5 1.5',
    'M11 13C6.5 12.6 3.6 10.6 2 7.5',
    'M11.5 17C7.6 17 5 15.6 3.5 13',
  ]

  return (
    <svg
      viewBox="0 0 48 30"
      className={cn('h-6 w-auto shrink-0', className)}
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {title && <title>{title}</title>}
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="48" y2="30" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="var(--color-brand-teal)" />
          <stop offset="1" stopColor="var(--color-brand-cyan)" />
        </linearGradient>
      </defs>
      {/* Wings: left strokes, mirrored on the right */}
      <g stroke={`url(#${gradientId})`} strokeWidth="2.2">
        {wings.map((d) => (
          <path key={d} d={d} />
        ))}
        <g transform="matrix(-1 0 0 1 48 0)">
          {wings.map((d) => (
            <path key={d} d={d} />
          ))}
        </g>
      </g>
      {/* Open book */}
      <g stroke="currentColor" strokeWidth="2.2">
        <path d="M24 9.5C20.2 7.2 15.6 6.6 11.2 7.4L12.6 25.4C16.4 24.6 20.4 25.2 24 27.6C27.6 25.2 31.6 24.6 35.4 25.4L36.8 7.4C32.4 6.6 27.8 7.2 24 9.5Z" />
        <path d="M24 9.5V27.6" />
      </g>
    </svg>
  )
}

interface LogoProps {
  className?: string
  /** Hide the wordmark (e.g. collapsed rails) */
  markOnly?: boolean
  size?: 'md' | 'lg'
}

export function AgentWikiLogo({ className, markOnly = false, size = 'md' }: LogoProps) {
  const markClass = size === 'lg' ? 'h-9' : 'h-6'
  if (markOnly) return <AgentWikiMark className={cn(markClass, className)} title="AgentWiki" />

  return (
    <span className={cn('inline-flex items-center gap-2 text-fg', size === 'lg' && 'gap-3', className)}>
      <AgentWikiMark className={markClass} />
      <span className={cn('font-logo font-bold leading-none tracking-tight', size === 'lg' ? 'text-3xl' : 'text-lg')}>
        Agent
        <span className="bg-gradient-to-r from-brand-teal to-brand-cyan bg-clip-text text-transparent">Wiki</span>
      </span>
    </span>
  )
}
